/**
 * Фокус года: чем человек занят в этом году и как это сдвигает состояния.
 *
 * Таблица `FOCUS_DEFS` — данные (возрастные рамки, условия, эффекты);
 * `availableFocus` — выбор по возрасту и условиям; `applyFocus` — годовой
 * сдвиг состояний с шумом N(0,1) и клампом в [0, 100].
 * Денежные эффекты `work`/`career` — в economy.ts (Task 9).
 */
import type { Rng } from './rng';
import { clampStat, type Focus, type Life, type Stats } from './types';

export interface FocusDef {
  id: Focus;
  /** Нижняя граница возраста, включительно. */
  minAge: number;
  /** Верхняя граница возраста, включительно. */
  maxAge: number;
  /** Дополнительное условие доступности (работа, подопечный и т.п.). */
  requires?: (life: Life) => boolean;
  /** Годовой сдвиг состояний; пропущенные ключи = 0. */
  effects: Partial<Stats>;
  /** Ключ названия в каталоге i18n (`focus.<id>`). */
  labelKey: string;
}

const hasJob = (life: Life): boolean => life.job !== null;
const hasCareTarget = (life: Life): boolean => Boolean(life.flags['careTarget']);

export const FOCUS_DEFS: readonly FocusDef[] = [
  { id: 'study', minAge: 6, maxAge: 30, effects: { knowledge: 5, happiness: -1, relations: -1 }, labelKey: 'focus.study' },
  { id: 'play', minAge: 3, maxAge: 17, effects: { relations: 4, happiness: 4 }, labelKey: 'focus.play' },
  { id: 'helpFamily', minAge: 8, maxAge: 25, effects: { relations: 2, knowledge: -1 }, labelKey: 'focus.helpFamily' },
  { id: 'sport', minAge: 6, maxAge: 90, effects: { health: 4, happiness: 2, relations: 1 }, labelKey: 'focus.sport' },
  { id: 'hobby', minAge: 10, maxAge: 100, effects: { happiness: 4 }, labelKey: 'focus.hobby' },
  { id: 'work', minAge: 16, maxAge: 75, requires: hasJob, effects: { health: -3, happiness: -2, relations: -2 }, labelKey: 'focus.work' },
  { id: 'partTime', minAge: 15, maxAge: 25, effects: { health: -1, happiness: -1, knowledge: -1 }, labelKey: 'focus.partTime' },
  { id: 'balance', minAge: 15, maxAge: 100, effects: { happiness: 1, health: 1, relations: 1 }, labelKey: 'focus.balance' },
  { id: 'family', minAge: 18, maxAge: 100, effects: { relations: 4, happiness: 3 }, labelKey: 'focus.family' },
  { id: 'rest', minAge: 40, maxAge: 100, effects: { health: 3, happiness: 2 }, labelKey: 'focus.rest' },
  { id: 'faith', minAge: 12, maxAge: 100, effects: { happiness: 3, relations: 2 }, labelKey: 'focus.faith' },
  { id: 'career', minAge: 22, maxAge: 65, requires: hasJob, effects: { happiness: -1, relations: -2 }, labelKey: 'focus.career' },
  { id: 'health', minAge: 30, maxAge: 100, effects: { health: 5 }, labelKey: 'focus.health' },
  { id: 'care', minAge: 18, maxAge: 100, requires: hasCareTarget, effects: { relations: 3, happiness: 1, health: -1 }, labelKey: 'focus.care' },
];

const FOCUS_BY_ID: ReadonlyMap<Focus, FocusDef> = new Map(FOCUS_DEFS.map((d) => [d.id, d]));

export function focusDef(id: Focus): FocusDef {
  const def = FOCUS_BY_ID.get(id);
  if (!def) throw new RangeError(`focusDef: unknown focus ${id}`);
  return def;
}

/** Фокусы, доступные в текущем возрасте и состоянии, в порядке таблицы. */
export function availableFocus(life: Life): Focus[] {
  const out: Focus[] = [];
  for (const def of FOCUS_DEFS) {
    if (life.age < def.minAge || life.age > def.maxAge) continue;
    if (def.requires && !def.requires(life)) continue;
    out.push(def.id);
  }
  return out;
}

/** Клампит все четыре состояния в [0, 100]; NaN → 0. Возвращает новый объект. */
export function clampStats(s: Stats): Stats {
  return {
    health: clampStat(s.health),
    happiness: clampStat(s.happiness),
    knowledge: clampStat(s.knowledge),
    relations: clampStat(s.relations),
  };
}

const STAT_KEYS: readonly (keyof Stats)[] = ['health', 'happiness', 'knowledge', 'relations'];

/**
 * Применяет текущий `life.focus`: к каждому состоянию прибавляет эффект
 * из таблицы плюс N(0, 1), затем клампит. Не мутирует вход.
 * Шум берётся из переданного `rng` в фиксированном порядке
 * health → happiness → knowledge → relations.
 */
export function applyFocus(life: Life, rng: Rng): Life {
  const { effects } = focusDef(life.focus);
  const next: Stats = { ...life.stats };
  for (const key of STAT_KEYS) {
    next[key] = life.stats[key] + (effects[key] ?? 0) + rng.normal(0, 1);
  }
  return { ...life, stats: clampStats(next) };
}
